import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { AiHelpService } from '../ai-help.service';
import { AiHelpVisibilityService } from '../ai-help-visibility.service';
import { AiHelpEvent, AiHelpMessage, AiHelpShortcut } from '../ai-help.models';
import { sanitizeHtml } from 'src/app/shared/utils/safe-html.util';

/**
 * Widget de ajuda com IA (how-to) — FAB no canto inferior ESQUERDO que abre um
 * drawer de chat. Porte do abcx-web (`shared/ai-help/`), reestilizado com a
 * identidade WAZIR (navy #14294E + ciano #00AEE0 — mesma paleta do rebrand em
 * `src/assets/sass/variables.sass` / `src/app/styles/material-theme.scss`).
 *
 * Diferença em relação ao abcx: o abcx ainda não tem a engrenagem global de
 * "ocultar ajuda nesta tela" (`specs/abcx/ai-help-fab-visibility.md`, item 2 —
 * decisão do P.O. de 13/jul, não implementada no momento deste porte). Como o
 * core-web também não tem um settings flutuante global equivalente, o
 * controle "ocultar/exibir ajuda nesta tela" fica no HEADER do drawer
 * (botão ⚙) — ver AiHelpVisibilityService. Quando a tela atual está oculta e
 * o drawer está fechado, o FAB fica MINIMIZADO (ícone pequeno, sem o rótulo
 * "Ajuda") em vez de sumir por completo — evita um beco sem saída (o usuário
 * sempre tem como reabrir o drawer para reverter a preferência daquela tela).
 */
@Component({
  selector: 'app-ai-help-widget',
  templateUrl: './ai-help-widget.component.html',
  styleUrls: ['./ai-help-widget.component.sass'],
})
export class AiHelpWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('scrollBody') private scrollBody?: ElementRef<HTMLElement>;

  open = false;
  sending = false;
  draft = '';
  messages: AiHelpMessage[] = [];
  currentUrl = '';

  readonly shortcuts: AiHelpShortcut[] = [
    { label: 'Fazer uma venda', question: 'Como fazer uma venda?' },
    { label: 'Lançar conta a pagar', question: 'Como lançar uma conta a pagar?' },
    { label: 'Controlar estoque', question: 'Como controlar o estoque?' },
  ];

  private streamSub?: Subscription;
  private routerSub?: Subscription;
  private shouldScroll = false;

  constructor(
    private readonly service: AiHelpService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly visibilityService: AiHelpVisibilityService,
  ) {}

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl = (event as NavigationEnd).urlAfterRedirects;
        // Trocar de tela fecha o drawer aberto na tela anterior (evita o
        // drawer "vazar" de uma rota para outra ainda aberto).
        this.open = false;
      });
  }

  /** Só exibe o widget para usuário autenticado (esconde na tela de login). */
  get isAuthenticated(): boolean {
    return !!this.authService.getAuthorizationToken?.();
  }

  /** Preferência do usuário para a rota atual — ver AiHelpVisibilityService. */
  get isHiddenOnScreen(): boolean {
    return this.visibilityService.isHidden(this.currentUrl);
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) this.shouldScroll = true;
  }

  close(): void {
    this.open = false;
  }

  /** Alterna "ocultar/exibir ajuda nesta tela" — botão ⚙ no header do drawer. */
  toggleVisibilityForScreen(): void {
    this.visibilityService.toggle(this.currentUrl);
  }

  askShortcut(shortcut: AiHelpShortcut): void {
    this.draft = shortcut.question;
    this.send();
  }

  send(): void {
    const question = this.draft.trim();
    if (!question || this.sending) return;

    // Snapshot do histórico ANTES de empurrar a pergunta atual: `question` já
    // vai no campo dedicado do payload, então incluí-la de novo dentro do
    // `history` duplicaria a última pergunta (achado do CodeRabbit no PR #70
    // — desperdiça tokens e pode confundir a IA sobre qual é a pergunta nova).
    const history = this.messages
      .filter((m) => !m.streaming)
      .slice(-6)
      .map((m) => ({ role: m.role, content: m.content }));

    this.messages.push({ role: 'user', content: question });
    const assistant: AiHelpMessage = {
      role: 'assistant',
      content: '',
      streaming: true,
    };
    this.messages.push(assistant);

    this.draft = '';
    this.sending = true;
    this.shouldScroll = true;

    this.streamSub?.unsubscribe();
    this.streamSub = this.service
      .streamChat({
        question,
        screenContext: this.currentUrl,
        history,
      })
      .subscribe({
        next: (event) => this.handleEvent(event, assistant),
        error: () => {
          assistant.streaming = false;
          if (!assistant.content) {
            assistant.content =
              'Não consegui responder agora. Tente novamente em instantes.';
          }
          this.sending = false;
          this.shouldScroll = true;
        },
        complete: () => {
          assistant.streaming = false;
          this.sending = false;
          this.shouldScroll = true;
        },
      });
  }

  private handleEvent(event: AiHelpEvent, assistant: AiHelpMessage): void {
    switch (event.type) {
      case 'meta':
        if (event.citation) assistant.citation = event.citation;
        // Se o backend sinalizar limite/indisponibilidade e a resposta
        // terminar sem `delta`, avisamos o usuário em vez de deixar a
        // bolha vazia. Não sobrescreve conteúdo já transmitido via `delta`.
        if ((event.aiUnavailable || event.error) && !assistant.content) {
          assistant.content =
            event.error === 'rate_limited' || event.error === 'quota_exceeded'
              ? 'Muitas solicitações agora. Tente novamente em instantes.'
              : 'A IA está indisponível no momento. Tente novamente em breve.';
        }
        break;
      case 'delta':
        assistant.content += event.text;
        this.shouldScroll = true;
        break;
      case 'cta':
        assistant.cta = {
          label: event.label,
          secondaryLabel: event.secondaryLabel,
          action: event.action,
          url: event.url,
        };
        break;
      case 'error':
        if (!assistant.content) assistant.content = event.message;
        break;
      case 'done':
        assistant.streaming = false;
        break;
    }
  }

  /**
   * O CTA vem do backend (constante estática hoje, mas nada impede que vire
   * dado configurável mais tarde) — só abrimos protocolos http/https, nunca
   * `javascript:`/`data:` etc. (achado de segurança do CodeRabbit no PR #70).
   */
  openCta(message: AiHelpMessage): void {
    const url = message.cta?.url;
    if (!url) return;

    let parsed: URL;
    try {
      parsed = new URL(url, window.location.origin);
    } catch {
      return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;

    window.open(parsed.href, '_blank', 'noopener');
  }

  /**
   * Markdown MUITO simples e XSS-safe: escapamos todo HTML primeiro, depois
   * aplicamos **negrito**, quebras de linha e listas numeradas/marcadas. Como o
   * escape roda antes, não há vetor de injeção mesmo que o conteúdo mudasse.
   */
  renderMarkdown(text: string): string {
    const escaped = (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const withBold = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    const lines = withBold.split('\n');
    let html = '';
    let listType: 'ol' | 'ul' | null = null;

    const closeList = () => {
      if (listType) {
        html += `</${listType}>`;
        listType = null;
      }
    };

    for (const line of lines) {
      const trimmed = line.trim();
      const orderedMatch = /^\d+\.\s+(.*)$/.exec(trimmed);
      const bulletMatch = /^[-*]\s+(.*)$/.exec(trimmed);

      if (orderedMatch) {
        if (listType !== 'ol') {
          closeList();
          html += '<ol>';
          listType = 'ol';
        }
        html += `<li>${orderedMatch[1]}</li>`;
      } else if (bulletMatch) {
        if (listType !== 'ul') {
          closeList();
          html += '<ul>';
          listType = 'ul';
        }
        html += `<li>${bulletMatch[1]}</li>`;
      } else if (trimmed === '') {
        closeList();
      } else {
        closeList();
        html += `<p>${line}</p>`;
      }
    }
    closeList();

    return sanitizeHtml(this.sanitizer, html);
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.scrollBody) {
      this.scrollBody.nativeElement.scrollTop =
        this.scrollBody.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }

  ngOnDestroy(): void {
    this.streamSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }
}
